import { useEffect, useState, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { AppState } from "@Store/index";

import ChevronRightIcon from "@Icons/ChevronRightIcon";
import useTranslationHook from "hooks/useTranslationHook";
import fetchPaymentInfo from "@Pages/Payment/components/PaymentSummary/helpers/fetchPaymentInfo";
import { updatePaymentSummaryForBookForm } from "@Pages/BookForm/data/bookFormReducer";
import { isEmptyObject } from "helpers/validations";
import PropertyDetailsModal from "@Pages/PropertyPage/components/PropertyDetailsModal";
import PriceSummaryContent from "../PriceSummaryContent";

import styles from "./PriceSummaryWrapper.module.scss";
import { useLocation } from "react-router";
import wrapperHOC from "@Utils/wrapperHOC";

// ============================================================================
// Type Definitions
// ============================================================================

interface PriceSummaryWrapperProps {
  /**
   * Whether payment functionality is active
   */
  paymentActive?: boolean;

  /**
   * Whether the component is being rendered on desktop
   */
  isDesktop?: boolean;
  /**
   * Custom className for the divider
   */
  dividerClassName?: string;

  /**
   * Custom className for the view price summary button
   */
  buttonClassName?: string;

  /**
   * Custom className for the button text
   */
  buttonTextClassName?: string;

  /**
   * Custom button text (defaults to "View Price Summary")
   */
  buttonText?: string;

  /**
   * Whether to show the divider above the button
   */
  showDivider?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Checks if payment summary contains relevant fee types
 */
const hasRelevantFees = (): boolean => true; // made this true to allow for all payment types

// ============================================================================
// Main Component
// ============================================================================

const PriceSummaryWrapper = ({
  paymentActive = true,
  isDesktop = false,
  dividerClassName = "",
  buttonClassName = "",
  buttonTextClassName = "",
  buttonText,
  showDivider = true,
}: PriceSummaryWrapperProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();

  // State
  const [showPriceSummaryModal, setShowPriceSummaryModal] = useState(false);
  const [hasRelevantPaymentFees, setHasRelevantPaymentFees] = useState(false);
  const [storedNationality, setStoredNationality] = useState<string>("");
  const [storedTenancyId, setStoredTenancyId] = useState<string>("");
  const [storedRoomType, setStoredRoomType] = useState<string>("");
  const [storedInventoryId, setStoredInventoryId] = useState<string>("");

  // Redux selectors
  const {
    inventory,
    accommodationDetails,
    personalDetails,
    nationality,
    paymentSummaryForBookForm,
    paymentInfoFromPayment,
    userCurrency,
  } = useSelector(
    ({ bookFormData, appData }: AppState) => ({
      inventory: bookFormData?.data,
      accommodationDetails: bookFormData?.formData?.accommodationDetails,
      accommodationDetailsFromPayment: bookFormData.paymentData,
      personalDetails: bookFormData?.formData?.personalDetails,
      paymentSummaryForBookForm: bookFormData.paymentSummaryForBookForm,
      paymentInfoFromPayment: bookFormData.paymentInfo.payment_info,
      nationality: appData.nationality,
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );
  const location = useLocation();
  const locationParams = new URLSearchParams(location.search);
  const nationalityParams = locationParams.get("nationality");
  // Derived values
  const nationalityForPaymentSummary =
    nationalityParams || personalDetails?.["data/nationality"] || nationality || "";

  const iEnabledPriceSummary = true;

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const getPaymentInfoAndSummaryObject = useCallback(async () => {
    const tenancyId =
      accommodationDetails?.tenancyId ||
      accommodationDetails?.roomId ||
      accommodationDetails?.inventory_id;

    if (!inventory?.canonical_name || !tenancyId) {
      return;
    }

    try {
      const isPaymentInfoFromPaymentAvailable = paymentInfoFromPayment?.length > 0;
      if (isPaymentInfoFromPaymentAvailable) {
        dispatch(updatePaymentSummaryForBookForm(paymentInfoFromPayment));
        // Check if payment info contains relevant fee types
        const hasRelevantFeesResult = hasRelevantFees();
        setHasRelevantPaymentFees(hasRelevantFeesResult);
      } else {
        const res = await fetchPaymentInfo({
          id: inventory.canonical_name,
          inventory_id: tenancyId,
          source: "inventoryCard",
          nationality: nationalityForPaymentSummary,
          userCurrency,
        });
        if (!isEmptyObject(res)) {
          dispatch(updatePaymentSummaryForBookForm(res?.payment_info));

          // Check if payment info contains relevant fee types
          const hasRelevantFeesResult = hasRelevantFees();
          setHasRelevantPaymentFees(hasRelevantFeesResult);
        }
      }
    } catch (error) {
      console.error("Error fetching payment info in PriceSummaryWrapper:", error);
    }
  }, [
    inventory?.canonical_name,
    accommodationDetails?.tenancyId,
    accommodationDetails?.roomId,
    accommodationDetails?.inventory_id,
    nationalityForPaymentSummary,
  ]);

  // Handle initial load, nationality changes, and accommodation detail changes
  useEffect(() => {
    // Only fetch if paymentActive is true
    if (!paymentActive) {
      return;
    }

    const currentTenancyId = accommodationDetails?.tenancyId || "";
    const currentRoomType = accommodationDetails?.roomType || "";
    const currentInventoryId = accommodationDetails?.inventory_id || "";

    // Check if nationality has changed from stored value
    const isNationalityChanged =
      nationalityForPaymentSummary &&
      storedNationality &&
      nationalityForPaymentSummary !== storedNationality;

    // Check if accommodation details have changed
    const isTenancyIdChanged =
      currentTenancyId && storedTenancyId && currentTenancyId !== storedTenancyId;
    const isRoomTypeChanged =
      currentRoomType && storedRoomType && currentRoomType !== storedRoomType;
    const isInventoryIdChanged =
      currentInventoryId && storedInventoryId && currentInventoryId !== storedInventoryId;

    // Check if this is initial load with nationality
    const isInitialLoadWithNationality = nationalityForPaymentSummary && !storedNationality;

    // Check if this is initial load with accommodation details
    const isInitialLoadWithTenancyId = currentTenancyId && !storedTenancyId;
    const isInitialLoadWithRoomType = currentRoomType && !storedRoomType;
    const isInitialLoadWithInventoryId = currentInventoryId && !storedInventoryId;

    // Determine if we should fetch
    const shouldFetch =
      isNationalityChanged ||
      isTenancyIdChanged ||
      isRoomTypeChanged ||
      isInventoryIdChanged ||
      isInitialLoadWithNationality ||
      isInitialLoadWithTenancyId ||
      isInitialLoadWithRoomType ||
      isInitialLoadWithInventoryId;

    // Always fetch when any relevant data changes or on initial load
    if (shouldFetch) {
      getPaymentInfoAndSummaryObject();
      setStoredNationality(nationalityForPaymentSummary);
      setStoredTenancyId(currentTenancyId);
      setStoredRoomType(currentRoomType);
      setStoredInventoryId(currentInventoryId);
    } else if (
      !nationalityForPaymentSummary &&
      !storedNationality &&
      !currentTenancyId &&
      !currentRoomType &&
      !currentInventoryId
    ) {
      // Fallback: fetch if no nationality, no accommodation details, and no data
      const hasNoPaymentSummary =
        isEmptyObject(paymentSummaryForBookForm) ||
        paymentSummaryForBookForm === undefined ||
        (Array.isArray(paymentSummaryForBookForm) && paymentSummaryForBookForm.length < 1);

      if (hasNoPaymentSummary) {
        getPaymentInfoAndSummaryObject();
      }
    }
  }, [
    nationalityForPaymentSummary,
    storedNationality,
    storedTenancyId,
    storedRoomType,
    storedInventoryId,
    paymentSummaryForBookForm,
    getPaymentInfoAndSummaryObject,
    paymentActive,
    accommodationDetails?.roomType,
    accommodationDetails?.tenancyId,
    accommodationDetails?.inventory_id,
  ]);

  // Check for relevant fees on mount
  useEffect(() => {
    const hasRelevantFeesResult = hasRelevantFees();
    setHasRelevantPaymentFees(hasRelevantFeesResult);
  }, [paymentSummaryForBookForm]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleViewPriceSummary = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPriceSummaryModal(true);
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    if (e && typeof e === "object" && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    setShowPriceSummaryModal(false);
  };

  // ============================================================================
  // Visibility Logic
  // ============================================================================

  const shouldShowPriceSummary = Boolean(
    (accommodationDetails?.tenancyId ||
      (accommodationDetails?.arrival_date && accommodationDetails?.roomType)) &&
      accommodationDetails?.duration &&
      paymentActive &&
      hasRelevantPaymentFees &&
      iEnabledPriceSummary,
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!shouldShowPriceSummary) {
    return null;
  }

  return (
    <>
      {isDesktop ? (
        <>
          {showDivider && <div className={dividerClassName || styles.divider} />}

          <div
            className={buttonClassName || styles.viewPriceSummary}
            onClick={handleViewPriceSummary}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleViewPriceSummary(e as any);
              }
            }}
          >
            <span className={buttonTextClassName || styles.viewPriceSummaryText}>
              {buttonText || t("View Price Summary")}
            </span>
            <ChevronRightIcon width={16} height={16} color="#111928" />
          </div>
        </>
      ) : (
        <div className={styles.viewPriceSummaryMobile} onClick={handleViewPriceSummary} role="none">
          <span
            className={styles.viewPriceSummaryTextMobile}
            style={{ textDecoration: "underline" }}
          >
            {t("View Price Summary")}
          </span>
          {/* <ChevronRightIcon width={7} height={15} color="#111928" /> */}
        </div>
      )}

      {showPriceSummaryModal && (
        <PropertyDetailsModal
          header={t("formlayout.inventorycard.priceSummaryWrapper.pricesummarytext")}
          hidePropertyModal={handleCloseModal}
          isDesktop={isDesktop}
          modalClassName={styles.modalSize}
          bodyWithEvenPadding
        >
          <PriceSummaryContent />
        </PropertyDetailsModal>
      )}
    </>
  );
};

export default wrapperHOC(PriceSummaryWrapper, {
  componentName: "PriceSummaryWrapper",
  showForChina: false,
});
